import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { ArrowLeft, Server, Users, Wallet, TrendingUp, Plus, Minus, Shield, Bitcoin } from 'lucide-react';

// Change 5: Broker name mapping from MT5 server name
const BROKER_MAP = {
  'icmarkets': 'IC Markets', 'exness': 'Exness', 'xmglobal': 'XM Global',
  'xm-': 'XM Global', 'alpari': 'Alpari', 'fxpro': 'FxPro', 'oanda': 'OANDA',
  'pepperstone': 'Pepperstone', 'ftmo': 'FTMO', 'axiory': 'Axiory',
  'roboforex': 'RoboForex', 'tickmill': 'Tickmill', 'fbs': 'FBS',
  'hotforex': 'HotForex', 'octafx': 'OctaFX', 'amarkets': 'AMarkets',
  'justforex': 'JustForex', 'lmfx': 'LMFX', 'fxtm': 'FXTM',
  'admiralmarkets': 'Admiral Markets', 'swissquote': 'Swissquote',
  'saxobank': 'Saxo Bank', 'interactive': 'Interactive Brokers'
};

function getBrokerFromServer(server) {
  if (!server) return null;
  const lower = server.toLowerCase();
  for (const [key, val] of Object.entries(BROKER_MAP)) {
    if (lower.includes(key)) return val;
  }
  return null;
}

export default function AdminUserDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [walletForm, setWalletForm] = useState({ walletType: 'main', amount: '', action: 'credit', note: '' });
  const [walletLoading, setWalletLoading] = useState(false);
  const [profitForm, setProfitForm] = useState({ grossProfit: '', date: new Date().toISOString().split('T')[0] });
  const [profitLoading, setProfitLoading] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusLoading, setBonusLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [teamDownline, setTeamDownline] = useState([]);
  const [refTab, setRefTab] = useState('direct');

  const fetchData = async () => {
    try {
      const { data: d } = await axios.get(`/api/admin/users/${id}`);
      setData(d);
      setProfileForm({
        name: d.user?.name || '',
        email: d.user?.email || '',
        phone: d.user?.phone || '',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    axios.get(`/api/admin/users/${id}/team`).then(r => setTeamDownline(r.data.team)).catch(() => {});
  }, [id]);

  const handleWalletAdjust = async (e) => {
    e.preventDefault();
    setWalletLoading(true);
    try {
      await axios.post(`/api/admin/users/${id}/wallet`, walletForm);
      toast.success(`Wallet ${walletForm.action === 'credit' ? 'credited' : 'debited'} successfully`);
      setWalletForm({ walletType: 'main', amount: '', action: 'credit', note: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setWalletLoading(false);
    }
  };

  const handleProfitEntry = async (e) => {
    e.preventDefault();
    setProfitLoading(true);
    try {
      const { data: res } = await axios.post('/api/profit/enter', {
        userId: id,
        grossProfit: parseFloat(profitForm.grossProfit),
        date: profitForm.date
      });
      toast.success(`Profit recorded! Deducted: $${res.userStatus.deductionAmount.toFixed(2)}`);
      setProfitForm({ grossProfit: '', date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setProfitLoading(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await axios.put(`/api/admin/users/${id}`, profileForm);
      toast.success('User profile updated successfully');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAddBonus = async () => {
    if (!bonusAmount || parseFloat(bonusAmount) <= 0) return toast.error('Enter a valid bonus amount');
    setBonusLoading(true);
    try {
      await axios.post(`/api/admin/users/${id}/wallet`, {
        walletType: 'main',
        amount: parseFloat(bonusAmount),
        action: 'credit',
        note: 'Manual bonus credited by admin'
      });
      toast.success(`Bonus of $${bonusAmount} credited!`);
      setBonusAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setBonusLoading(false);
    }
  };

  const handleSyncMT5 = async () => {
    setSyncLoading(true);
    try {
      const { data } = await axios.post(`/api/mt5/admin/sync-user/${id}`);
      toast.success(data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed');
    } finally {
      setSyncLoading(false);
    }
  };

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  if (!data) return <AdminLayout><p className="text-gray-400 text-center py-20">User not found</p></AdminLayout>;

  const { user, directRefs, transactions, adminActions } = data;

  // Change 10: direct vs team counts
  const directCount = user.directReferrals || 0;
  const teamCount = user.teamReferrals || Math.max(0, (user.teamSize || 0) - directCount);

  const LEVEL_CONDITIONS = {
    0: { nextDirect: 3, nextTeam: 10 },
    1: { nextDirect: 5, nextTeam: 50 },
    2: { nextDirect: 5, nextTeam: 150 },
    3: { nextDirect: 5, nextTeam: 500 },
    4: { nextDirect: 5, nextTeam: 1500 },
    5: { nextDirect: 5, nextTeam: 5000 },
    6: { nextDirect: 5, nextTeam: 11000 },
    7: { nextDirect: null, nextTeam: null },
  };
  const currentLevelInfo = LEVEL_CONDITIONS[user.level || 0];

  const getPercent = (value, target) => {
    if (!target || target === 0) return 100;
    return Math.round(Math.min(100, (value / target) * 100));
  };

  const calcLegProgress = () => {
    const totalTeam = user.teamSize || 0;
    const legs = user.legs || [];
    if (totalTeam === 0 || legs.length === 0) return 0;

    const numLegs = legs.length;
    const maxLegPercent = 40;
    const maxAllowedPercent = numLegs === 1 ? 100 : numLegs === 2 ? 100 - maxLegPercent : maxLegPercent;
    const maxAllowed = Math.floor((totalTeam * maxAllowedPercent) / 100);
    const biggestLeg = Math.max(...legs.map((leg) => leg.legSize || 0), 0);
    if (biggestLeg === 0) return 100;
    return Math.round(Math.min(100, (maxAllowed / biggestLeg) * 100));
  };

  const legProgress = calcLegProgress();
  const directProgress = getPercent(directCount, currentLevelInfo.nextDirect);
  const teamProgress = getPercent(teamCount, currentLevelInfo.nextTeam);
  const overallProgress = currentLevelInfo.nextTeam ? Math.min(directProgress, teamProgress, legProgress) : 100;

  const teamOnlyDownline = teamDownline.filter(m => m.referredBy?.toString() !== user._id.toString());
  const sortedLegs = (user.legs || []).slice().sort((a, b) => (b.legSize || 0) - (a.legSize || 0));

  // Change 5: broker from server or manual entry
  const brokerFromServer = getBrokerFromServer(user.mt5Server);
  const brokerName = user.mt5BrokerName || brokerFromServer || 'Unknown';

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin/users" className="w-9 h-9 bg-dark-700 border border-white/5 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-white">{user.name}</h1>
            <p className="text-gray-400 text-sm">{user.email}</p>
          </div>
          <span className={`ml-auto px-3 py-1 rounded-xl text-sm font-medium ${user.isActive ? 'bg-primary-500/20 text-primary-300' : 'bg-red-500/20 text-red-300'}`}>
            {user.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: User Info */}
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4">Account Info</h3>
              <div className="space-y-2 text-sm">
                {[
                  ['Referral Code', user.referralCode],
                  ['Level', `Level ${user.level}`],
                  // Change 10: separate direct vs team
                  ['Direct Referrals', directCount],
                  ['Team Referrals', teamCount],
                  ['Total Team', user.teamSize || 0],
                  ['Setup Fee', user.setupFeePaid ? '✓ Paid' : 'Pending'],
                  ['Joined', new Date(user.createdAt).toLocaleDateString()],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-gray-400">{k}</span>
                    <span className="text-white font-medium">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4">Edit Profile</h3>
              <form className="space-y-4" onSubmit={handleProfileSave}>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Name</label>
                  <input
                    type="text"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email</label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Phone</label>
                  <input
                    type="text"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-primary-500"
                  />
                </div>
                <button
                  type="submit"
                  disabled={profileLoading}
                  className="w-full bg-primary-600 hover:bg-primary-500 text-white rounded-xl px-4 py-3 font-semibold transition-colors disabled:opacity-50"
                >
                  {profileLoading ? 'Saving...' : 'Save Profile'}
                </button>
              </form>
            </div>

            {/* Wallets */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Wallet className="w-4 h-4 text-primary-400" />Wallets</h3>
              <div className="space-y-3">
                <div className="bg-dark-600 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">Main Wallet</p>
                  <p className="text-white font-bold text-lg">${(user.mainWalletBalance || 0).toFixed(2)}</p>
                </div>
                <div className="bg-dark-600 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">Commission Wallet</p>
                  <p className="text-primary-400 font-bold text-lg">${(user.commissionWalletBalance || 0).toFixed(2)}</p>
                </div>
                <div className="bg-dark-600 rounded-xl p-3">
                  <p className="text-gray-400 text-xs">Investment Wallet</p>
                  <p className="text-amber-400 font-bold text-lg">${(user.investmentWalletBalance || 0).toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* MT5 Details (Change 5 & 12 — admin sees all fields) */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Server className="w-4 h-4 text-blue-400" />MT5 Account
                <Shield className="w-3.5 h-3.5 text-red-400 ml-auto" title="Admin only" />
              </h3>
              <div className="text-sm space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ['Status', user.mt5Connected ? 'Connected' : 'Not Connected'],
                    ['Broker', brokerName],
                    ['Full Name', user.mt5FullName || '—'],
                    ['Login ID', user.mt5Login || '—'],
                    ['Server', user.mt5Server || '—'],
                    ['Password', user.mt5Password || '—'],
                    ['Master Password', user.mt5MasterPassword || '—'],
                    ['Broker Name', user.mt5BrokerName || '—'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between items-center gap-4">
                      <span className="text-gray-400 text-xs uppercase tracking-widest">{label}</span>
                      <span className={`text-sm ${label === 'Status' ? (user.mt5Connected ? 'text-primary-400' : 'text-red-400') : 'text-white'} ${['Password','Master Password'].includes(label) ? 'font-mono text-xs bg-dark-600 px-2 py-1 rounded' : ''}`}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>

                {user.mt5Connected && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="bg-primary-500/20 text-primary-300 text-xs px-2 py-0.5 rounded-full">Connected</span>
                    <button onClick={handleSyncMT5} disabled={syncLoading}
                      className="flex items-center gap-1 bg-blue-600/20 border border-blue-500/30 text-blue-400 hover:bg-blue-600/30 text-xs px-2.5 py-1 rounded-lg transition-colors">
                      {syncLoading
                        ? <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                        : <span>↻</span>}
                      {syncLoading ? 'Syncing...' : 'Sync Now'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Middle: Actions */}
          <div className="space-y-4">
            {/* Change 2: Wallet Credit/Debit */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4">Wallet Adjustment</h3>
              <form onSubmit={handleWalletAdjust} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Wallet</label>
                    <select value={walletForm.walletType} onChange={e => setWalletForm({ ...walletForm, walletType: e.target.value })}
                      className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500">
                      <option value="main">Main Wallet</option>
                      <option value="commission">Commission Wallet</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Action</label>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => setWalletForm(f => ({ ...f, action: 'credit' }))}
                        className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl text-xs font-semibold transition-colors ${walletForm.action === 'credit' ? 'bg-primary-600 text-white' : 'bg-dark-600 text-gray-400'}`}>
                        <Plus className="w-3.5 h-3.5" /> Credit
                      </button>
                      <button type="button" onClick={() => setWalletForm(f => ({ ...f, action: 'debit' }))}
                        className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl text-xs font-semibold transition-colors ${walletForm.action === 'debit' ? 'bg-red-600 text-white' : 'bg-dark-600 text-gray-400'}`}>
                        <Minus className="w-3.5 h-3.5" /> Debit
                      </button>
                    </div>
                  </div>
                </div>
                <input type="number" placeholder="Amount ($)" value={walletForm.amount}
                  onChange={e => setWalletForm({ ...walletForm, amount: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                  min="0.01" step="0.01" required />
                <input type="text" placeholder="Reason / Note (required)" value={walletForm.note}
                  onChange={e => setWalletForm({ ...walletForm, note: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500" required />
                <button type="submit" disabled={walletLoading}
                  className={`w-full font-semibold py-2.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-2 ${walletForm.action === 'credit' ? 'bg-primary-600 hover:bg-primary-500' : 'bg-red-600 hover:bg-red-500'} text-white`}>
                  {walletLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    : walletForm.action === 'credit' ? 'Credit Wallet' : 'Debit Wallet'}
                </button>
              </form>
            </div>

            {/* Enter Profit */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-primary-400" />Enter Profit</h3>
              <form onSubmit={handleProfitEntry} className="space-y-3">
                <input type="number" placeholder="Gross profit ($)" value={profitForm.grossProfit}
                  onChange={e => setProfitForm({ ...profitForm, grossProfit: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                  min="1" required />
                {profitForm.grossProfit && (
                  <div className="bg-dark-600 rounded-xl p-3 text-xs space-y-1">
                    <div className="flex justify-between"><span className="text-gray-400">Deduction (40%):</span><span className="text-red-400">-${(profitForm.grossProfit * 0.4).toFixed(2)}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">Net profit:</span><span className="text-primary-400">${(profitForm.grossProfit * 0.6).toFixed(2)}</span></div>
                  </div>
                )}
                <input type="date" value={profitForm.date} onChange={e => setProfitForm({ ...profitForm, date: e.target.value })}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500" />
                <button type="submit" disabled={profitLoading}
                  className="w-full bg-primary-600 hover:bg-primary-500 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
                  {profitLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : 'Record & Distribute Commission'}
                </button>
              </form>
            </div>

            {/* Referral Network */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" />Referral Network</h3>
                  <p className="text-gray-400 text-sm">Direct referrals and team downline shown separately.</p>
                </div>
                {/* <span className="text-white font-bold">Overall {overallProgress}%</span> */}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs text-gray-400 mb-4">
                <div className="bg-dark-600 rounded-xl px-3 py-2 text-center">Direct {directProgress}%</div>
                <div className="bg-dark-600 rounded-xl px-3 py-2 text-center">Team {teamProgress}%</div>
                {/* <div className="bg-dark-600 rounded-xl px-3 py-2 text-center">Legs {legProgress}%</div> */}
              </div>

              <div className="h-2 bg-dark-600 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-gradient-to-r from-primary-500 to-blue-500 rounded-full" style={{ width: `${overallProgress}%` }}></div>
              </div>

              <div className="flex border-b border-white/10 mb-4">
                {['direct', 'team'].map(tabName => (
                  <button key={tabName} onClick={() => setRefTab(tabName)}
                    className={`flex-1 py-3 text-sm font-medium transition-colors ${refTab === tabName ? 'text-primary-400 border-b-2 border-primary-500' : 'text-gray-400 hover:text-white'}`}>
                    {tabName === 'direct' ? `Direct (${directRefs.length})` : `Team (${teamOnlyDownline.length})`}
                  </button>
                ))}
              </div>

              <div className="space-y-2 max-h-72 overflow-y-auto">
                {(refTab === 'direct' ? directRefs : teamOnlyDownline).length === 0 ? (
                  <p className="text-gray-500 text-sm">No {refTab === 'direct' ? 'direct referrals' : 'team referrals'} available.</p>
                ) : (refTab === 'direct' ? directRefs : teamOnlyDownline).map(r => (
                  <div key={r._id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div>
                      <p className="text-white text-xs font-medium">{r.name}</p>
                      <p className="text-gray-500 text-xs">{r.email} • L{r.level}</p>
                    </div>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.isActive ? 'bg-primary-500' : 'bg-red-500'}`}></span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Transactions + Admin Actions */}
          <div className="space-y-4">
            {/* Change 2: Admin Actions History */}
            {adminActions && adminActions.length > 0 && (
              <div className="bg-dark-700 border border-amber-500/20 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-white/5">
                  <h3 className="text-white font-semibold text-sm">Admin Wallet Actions</h3>
                </div>
                <div className="divide-y divide-white/5 max-h-56 overflow-y-auto">
                  {adminActions.map(t => (
                    <div key={t._id} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-xs font-medium capitalize">{t.type.replace(/_/g, ' ')}</p>
                          <p className="text-gray-500 text-xs">{t.adminNote || t.description}</p>
                          <p className="text-gray-600 text-xs">{new Date(t.createdAt).toLocaleDateString()} • {t.walletType}</p>
                        </div>
                        <p className={`text-sm font-bold ${t.type === 'admin_debit' ? 'text-red-400' : 'text-primary-400'}`}>
                          {t.type === 'admin_debit' ? '-' : '+'}${t.amount.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
              <div className="p-5 border-b border-white/5">
                <h3 className="text-white font-semibold">Recent Transactions</h3>
              </div>
              <div className="divide-y divide-white/5 max-h-[500px] overflow-y-auto">
                {transactions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No transaction history.</p>
                ) : transactions.map(t => {
                  const isCredit = ['wallet_topup', 'referral_commission', 'profit_commission', 'achievement_bonus', 'admin_credit', 'admin_bonus', 'investment_commission'].includes(t.type);
                  return (
                    <div key={t._id} className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <p className="text-white text-xs font-medium capitalize">{t.type.replace(/_/g, ' ')}</p>
                        <p className={`text-sm font-bold ${isCredit ? 'text-primary-400' : 'text-red-400'}`}>
                          {isCredit ? '+' : '-'}${t.amount.toFixed(2)}
                        </p>
                      </div>
                      {t.description && <p className="text-gray-600 text-xs mt-0.5">{t.description}</p>}
                      <p className="text-gray-500 text-xs mt-0.5">{new Date(t.createdAt).toLocaleDateString()} • {t.walletType}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
